import type { Reactive } from 'vue'
import { reqOrder, postBuySubAccount } from '~/api/module/rights.ts'
import type { IOrder } from '~/api/interface/order.ts'
import { PAY_STATUS, PAY_TYPE } from '~/api/interface/rights'

interface IBuySubAccountOrderBody {
  num: number
}

export default function useOrder(orderBody: Reactive<IBuySubAccountOrderBody>) {
  const { $t } = useNuxtApp()
  const userInfo = useUserInfo()

  let timer: number = -1
  const abort = ref<AbortController>()
  const creating = ref(false)
  const order = ref<IOrder>()
  const payType = ref(PAY_TYPE.AIRWALLEX)
  const POLLING_INTERVAL = 3000

  const currentRequest = ref()

  function stopPolling() {
    clearInterval(timer)
    timer = -1
  }

  function startPolling() {
    if (timer === -1) {
      timer = window.setInterval(checkOrder, POLLING_INTERVAL)
    }
  }

  async function checkOrder() {
    if (!order.value) {
      return
    }

    const res = await reqOrder(order.value.id)
    if (!res.code && res.data) {
      if (![PAY_STATUS.PAYING, PAY_STATUS.UNPAID].includes(res.data.status)) {
        order.value.status = res.data.status
        stopPolling()
      }
    }
  }

  async function createOrder() {
    stopPolling()

    // 退出正在进行 pending 的请求
    if (abort.value) {
      abort.value.abort()
    }

    abort.value = new AbortController()

    creating.value = true

    currentRequest.value = postBuySubAccount(
      {
        num: orderBody.num,
        pay_type: payType.value,
        first_name: $t('yong-hu'),
        last_name: userInfo.value?.account_name,
        email: userInfo.value?.email_address || '',
      },
      {
        signal: abort.value.signal,
      }
    )

    const res = await currentRequest.value

    if (!res.data) {
      return
    }

    order.value = res.data
    creating.value = false

    startPolling()
  }

  watch(
    () => orderBody,
    () => {
      if (abort.value) {
        abort.value.abort()
      }
      createOrder()
    },
    {
      deep: true,
      immediate: true,
    }
  )

  onBeforeUnmount(() => {
    if (abort.value) {
      abort.value.abort()
    }
    stopPolling()
  })

  return {
    order,
    creating,
  }
}
